import { Stack } from '@startuptools/ui/Stack';
import { Avatar } from '@startuptools/ui/Avatar';
import { Typography } from '@startuptools/ui/Typography';
import { ColorPaletteProp } from '@mui/joy/styles';
import { ComponentProps, PropsWithChildren, ReactElement } from 'react';
import { Trans, useLingui } from '@lingui/react';
import { GqlSeverity, GqlValidatorStatus } from '../../../graphql/base-types.graphql';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import CheckSharpIcon from '@mui/icons-material/CheckSharp';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { useStepperStep } from '../common/StepperStepContext';
import { useStepper } from '../common/StepperContext';

const StepIndicator = ({
  validatorStatus,
  isCurrentStep,
  stepNumber,
}: {
  validatorStatus?: GqlValidatorStatus | null;
  isCurrentStep: boolean;
  stepNumber: string;
}) => {
  const { i18n } = useLingui();
  const severityColor: ColorPaletteProp = validatorStatus?.severity === GqlSeverity.Error ? 'danger' : 'warning';
  const errorIcon =
    validatorStatus?.severity === GqlSeverity.Error ? <ErrorIcon color="error" /> : <WarningIcon color="warning" />;

  return (
    <Tooltip color={severityColor} title={validatorStatus?.error?.[i18n.locale === 'sv' ? 'sv' : 'en']?.short}>
      <Avatar
        variant="outlined"
        size="sm"
        color={
          validatorStatus?.hasError
            ? severityColor
            : isCurrentStep
              ? 'primary'
              : validatorStatus?.completed
                ? 'success'
                : 'neutral'
        }
      >
        {validatorStatus?.hasError ? (
          errorIcon
        ) : validatorStatus?.completed && !isCurrentStep ? (
          <CheckSharpIcon />
        ) : (
          stepNumber
        )}
      </Avatar>
    </Tooltip>
  );
};

export const StateStepperStep = ({
  label,
  id,
  validatorStatus,
}: PropsWithChildren<{
  label: ReactElement<ComponentProps<typeof Trans>>;
  id: string;
  validatorStatus: GqlValidatorStatus | null | undefined;
}>) => {
  const { stepTo, currentStep } = useStepper();
  const { stepNumber } = useStepperStep();
  const isCurrentStep = currentStep === id;

  return (
    <Stack
      key={id}
      component="button"
      onClick={() => stepTo(id)}
      direction="row"
      gap={1}
      alignItems="center"
      mx={1.5}
      p={0.5}
      sx={theme => ({
        backgroundColor: isCurrentStep ? theme.palette.background.level1 : 'inherit',
        cursor: 'pointer',
        borderRadius: 6,
        borderWidth: 0,
      })}
    >
      <StepIndicator validatorStatus={validatorStatus} stepNumber={stepNumber} isCurrentStep={isCurrentStep} />
      <Stack gap={1} alignItems="flex-start">
        <Typography level="title-sm" noWrap>
          {label}
        </Typography>
      </Stack>
    </Stack>
  );
};
